import React, { useState } from "react";
import { Send, CheckCircle2, MapPin, Mail, Phone } from "lucide-react";

const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    organisation: "",
    email: "",
    message: "",
  });
  const [sent, setSent] = useState(false);

  const onSubmit = (e) => {
    e.preventDefault();
    // Front-end only: simulate accept. Integrations can be plugged in later.
    setSent(true);
  };

  const disabled =
    !form.name.trim() || !form.email.trim() || !form.message.trim();

  return (
    <div data-testid="page-contact">
      <section className="rk-container pt-20 md:pt-28 pb-16">
        <div className="overline mb-6">Contact</div>
        <h1
          className="font-editorial text-5xl md:text-7xl text-white tracking-tight leading-[1.05] max-w-4xl"
          style={{ fontFamily: "Fraunces, serif", fontWeight: 300 }}
        >
          Begin een gesprek.{" "}
          <em className="italic text-[#D4D8DF]" style={{ fontFamily: "Fraunces, serif" }}>
            Geen demo — een dialoog.
          </em>
        </h1>
        <p className="mt-8 text-lg text-[#D4D8DF] max-w-2xl leading-relaxed">
          Laat weten welk vraagstuk uw gemeente aan het verkennen is. We
          reageren binnen twee werkdagen, meestal met een concreet vervolg.
        </p>
      </section>

      <div className="hairline" />

      <section className="rk-container py-16 md:py-24 grid grid-cols-1 md:grid-cols-12 gap-12">
        <aside className="md:col-span-4 space-y-8">
          <div>
            <div className="overline mb-2">Bezoek</div>
            <div className="flex items-start gap-3 text-[#D4D8DF]">
              <MapPin strokeWidth={1} className="w-4 h-4 mt-1 text-[#C0C5CE]" />
              <div>
                Hofplein 38
                <br />
                8911 HJ Leeuwarden
              </div>
            </div>
          </div>
          <div>
            <div className="overline mb-2">E-mail</div>
            <a
              href="mailto:hello@riskaia.nl"
              data-testid="contact-email-link"
              className="text-[#D4D8DF] hover:text-white flex items-center gap-3"
            >
              <Mail strokeWidth={1} className="w-4 h-4 text-[#C0C5CE]" />
              hello@riskaia.nl
            </a>
          </div>
          <div>
            <div className="overline mb-2">Telefoon</div>
            <div className="text-[#D4D8DF] flex items-center gap-3">
              <Phone strokeWidth={1} className="w-4 h-4 text-[#C0C5CE]" />
              +31 (0)58 123 4567
            </div>
          </div>
        </aside>

        <div className="md:col-span-7 md:col-start-6">
          {sent ? (
            <div
              data-testid="contact-success"
              className="border border-[#C0C5CE]/40 p-12 flex flex-col items-start gap-4"
            >
              <CheckCircle2 strokeWidth={1} className="w-10 h-10 text-[#9FE2BF]" />
              <h3
                className="font-editorial text-3xl text-white"
                style={{ fontFamily: "Fraunces, serif", fontWeight: 300 }}
              >
                Bericht ontvangen.
              </h3>
              <p className="text-[#D4D8DF] max-w-md">
                Bedankt {form.name || "—"}. We nemen binnen twee werkdagen
                contact met u op via {form.email}.
              </p>
              <button
                type="button"
                onClick={() => {
                  setSent(false);
                  setForm({ name: "", organisation: "", email: "", message: "" });
                }}
                className="rk-btn-ghost mt-4"
                data-testid="contact-reset"
              >
                Nieuw bericht
              </button>
            </div>
          ) : (
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="grid grid-cols-1 sm:grid-cols-2 gap-6"
            >
              <label className="flex flex-col gap-2 sm:col-span-1">
                <span className="overline">Naam</span>
                <input
                  data-testid="contact-name"
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  className="bg-transparent border-b border-[#C0C5CE]/40 focus:border-[#C0C5CE] py-2 text-white outline-none"
                />
              </label>
              <label className="flex flex-col gap-2 sm:col-span-1">
                <span className="overline">Organisatie</span>
                <input
                  data-testid="contact-organisation"
                  type="text"
                  value={form.organisation}
                  onChange={(e) => setForm({ ...form, organisation: e.target.value })}
                  className="bg-transparent border-b border-[#C0C5CE]/40 focus:border-[#C0C5CE] py-2 text-white outline-none"
                />
              </label>
              <label className="flex flex-col gap-2 sm:col-span-2">
                <span className="overline">E-mail</span>
                <input
                  data-testid="contact-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                  className="bg-transparent border-b border-[#C0C5CE]/40 focus:border-[#C0C5CE] py-2 text-white outline-none"
                />
              </label>
              <label className="flex flex-col gap-2 sm:col-span-2">
                <span className="overline">Bericht</span>
                <textarea
                  data-testid="contact-message"
                  rows={5}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  required
                  className="bg-transparent border border-[#C0C5CE]/40 focus:border-[#C0C5CE] p-4 text-white outline-none resize-none"
                />
              </label>
              <div className="sm:col-span-2 flex items-center justify-between gap-4 pt-2">
                <p className="text-xs text-[#9AA4B2] max-w-sm">
                  Door te versturen gaat u akkoord met onze privacyverklaring.
                  Gegevens worden uitsluitend gebruikt voor beantwoording.
                </p>
                <button
                  type="submit"
                  disabled={disabled}
                  data-testid="contact-submit"
                  className="rk-btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Versturen
                  <Send strokeWidth={1} className="w-4 h-4" />
                </button>
              </div>
            </form>
          )}
        </div>
      </section>
    </div>
  );
};

export default Contact;
